import { create } from 'zustand';
import type { Coupon, CreateCouponData, UpdateCouponData } from '../types';
import { couponService } from '../services/coupon.service';

interface CouponState {
  coupons: Coupon[];
  selectedCoupon: Coupon | null;
  isLoading: boolean;
  error: string | null;

  // Actions
  fetchCoupons: () => Promise<void>;
  getCouponById: (id: string) => Promise<Coupon>;
  createCoupon: (data: CreateCouponData) => Promise<Coupon>;
  updateCoupon: (id: string, data: UpdateCouponData) => Promise<Coupon>;
  deleteCoupon: (id: string) => Promise<void>;
  setSelectedCoupon: (coupon: Coupon | null) => void;
  clearError: () => void;
}

export const useCouponStore = create<CouponState>((set) => ({
  coupons: [],
  selectedCoupon: null,
  isLoading: false,
  error: null,

  fetchCoupons: async () => {
    set({ isLoading: true, error: null });
    try {
      const coupons = await couponService.getCoupons();
      set({ coupons, isLoading: false });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to fetch coupons';
      set({ error: message, isLoading: false });
    }
  },

  getCouponById: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      const coupon = await couponService.getCouponById(id);
      set({ selectedCoupon: coupon, isLoading: false });
      return coupon;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to fetch coupon';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  createCoupon: async (data: CreateCouponData) => {
    set({ isLoading: true, error: null });
    try {
      const newCoupon = await couponService.createCoupon(data);
      set((state) => ({
        coupons: [newCoupon, ...state.coupons],
        isLoading: false,
      }));
      return newCoupon;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to create coupon';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  updateCoupon: async (id: string, data: UpdateCouponData) => {
    set({ isLoading: true, error: null });
    try {
      const updatedCoupon = await couponService.updateCoupon(id, data);
      set((state) => ({
        coupons: state.coupons.map((c) => (c.id === id ? updatedCoupon : c)),
        selectedCoupon: state.selectedCoupon?.id === id ? updatedCoupon : state.selectedCoupon,
        isLoading: false,
      }));
      return updatedCoupon;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to update coupon';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  deleteCoupon: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      await couponService.deleteCoupon(id);
      set((state) => ({
        coupons: state.coupons.filter((c) => c.id !== id),
        selectedCoupon: state.selectedCoupon?.id === id ? null : state.selectedCoupon,
        isLoading: false,
      }));
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to delete coupon';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  setSelectedCoupon: (coupon: Coupon | null) => set({ selectedCoupon: coupon }),

  clearError: () => set({ error: null }),
}));
